/**
 * Early data prefetch for a cold page load.
 *
 * The SPA can only ask the API for a page's data once its JS bundle has been
 * downloaded, parsed and run — and most reads then wait for /api/site-config on
 * top. On a phone that is seconds of idle network. A tiny inline script in
 * index.html (injected by vite.config.ts) calls `prefetchUrlsFor` with the
 * page's path and starts those GETs while the bundle is still downloading;
 * `apiClient`'s fetcher then adopts each response instead of asking again
 * (`takePrefetched`).
 *
 * Signed-out visitors only: a signed-in read carries a bearer token and
 * per-account fields (`liked_by_me`, drafts), so an anonymous prefetch would be
 * the wrong answer for them — the inline script skips everything when a
 * Supabase session is stored.
 */

/**
 * The API GETs a signed-out cold load of `pathname` (with its query `search`)
 * makes, as the exact URLs `apiClient` builds. MUST stay self-contained (no
 * imports, no outer variables): vite.config.ts inlines its source text into
 * index.html.
 */
export function prefetchUrlsFor(pathname: string, apiBase: string, search = ""): string[] {
  const segments = pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => {
      try {
        return decodeURIComponent(segment);
      } catch {
        return segment;
      }
    });
  // Every route reads the site mode and the first catalog page.
  const urls = [apiBase + "/api/site-config", apiBase + "/api/novels?limit=24&offset=0"];
  const head = segments[0];
  const id = segments[1] ? encodeURIComponent(segments[1]) : "";
  // A short link key (<slug>-<code> / <code>, never "_" — lib/novelLinks.ts) is
  // resolved through GET /api/novels/<key>; its comments are then read by id.
  const isLinkKey = id.indexOf("_") < 0 && /(^|-)[2-9a-hjkmnp-z]{6}$/.test(id.toLowerCase());
  if ((head === "novel" || head === "play" || head === "n") && id) urls.push(apiBase + "/api/novels/" + id);
  if (head === "novel" && id && !isLinkKey) urls.push(apiBase + "/api/novels/" + id + "/comments");
  if (head === "u" && id) urls.push(apiBase + "/api/profiles/" + id);
  if (head === "pricing" && segments.length === 1) urls.push(apiBase + "/api/credits/prices");
  if (head === "catalog" && segments.length === 1) {
    urls.push(apiBase + "/api/catalog/facets");
    // A filtered catalog (/catalog?lang=ru&genre=horror) also reads its first
    // filtered page — normalized as lib/catalogFilters.ts parses the address
    // and apiClient's loadNovelCatalog builds the URL (the test pins both). The
    // genre table stays out of index.html, so this matches the addresses the
    // app writes (a genre's id, or free text); a hand-typed synonym («Ужасы»
    // for "horror") is prefetched as typed and simply goes unused.
    const params = new URLSearchParams(search);
    const lang = (params.get("lang") || "").trim().toLowerCase().split(/[-_]/)[0];
    const genre = (params.get("genre") || "").trim().replace(/\s+/g, " ").toLowerCase();
    const language = /^[a-z]{2,3}$/.test(lang) ? lang : "";
    const genreOk = genre.length > 0 && genre.length <= 40 && !/\p{Cc}/u.test(genre);
    if (language || genreOk) {
      urls.push(
        apiBase + "/api/novels?limit=24&offset=0" +
          (language ? "&language=" + encodeURIComponent(language) : "") +
          (genreOk ? "&genre=" + encodeURIComponent(genre) : "")
      );
    }
  }
  return urls;
}

/**
 * The inline `<script>` body vite.config.ts injects into index.html. Skips
 * signed-in visitors (a stored Supabase session) and a browser that last saw
 * the site in invite-only mode (its signed-out reads would all be refused), and
 * leaves `window.__lorvokPrefetch` in the shape `takePrefetched` reads; a failed
 * request resolves to null.
 */
export function prefetchBootstrapScript(apiBase: string): string {
  return (
    "(function(){try{var s=window.localStorage;" +
    'if(s.getItem("lorevok:site-config")==="closed")return;' +
    'for(var i=0;i<s.length;i++){if(/^sb-.+-auth-token$/.test(s.key(i)||""))return;}' +
    "var urlsFor=" + prefetchUrlsFor.toString() + ";var store={};" +
    "urlsFor(location.pathname," + JSON.stringify(apiBase) + ",location.search).forEach(function(u){" +
    "store[u]={at:Date.now(),response:fetch(u).catch(function(){return null;})};});" +
    "window.__lorvokPrefetch=store;}catch(e){}})();"
  );
}

/** How long after the page load a prefetched response may still be adopted. */
export const PREFETCH_TTL_MS = 60_000;

type PrefetchEntry = { at: number; response: Promise<Response | null> };

declare global {
  interface Window {
    __lorvokPrefetch?: Record<string, PrefetchEntry>;
  }
}

/**
 * The response the inline script already requested for `url`, handed out once.
 * Null when nothing was prefetched for it, it was already taken, it is older
 * than `PREFETCH_TTL_MS`, or the prefetch itself failed — the caller then just
 * fetches as usual. `signal` is the caller's own (a read timeout): the request
 * is already in flight and cannot take it, so waiting on it rejects when the
 * signal fires, exactly as the caller's `fetch` would have.
 */
export async function takePrefetched(
  url: string,
  signal?: AbortSignal | null,
  now = Date.now()
): Promise<Response | null> {
  if (typeof window === "undefined") return null;
  const store = window.__lorvokPrefetch;
  const entry = store?.[url];
  if (!store || !entry) return null;
  delete store[url];
  if (now - entry.at > PREFETCH_TTL_MS) return null;
  if (!signal) return entry.response;
  if (signal.aborted) throw signal.reason;
  return new Promise<Response | null>((resolve, reject) => {
    const onAbort = () => reject(signal.reason);
    signal.addEventListener("abort", onAbort, { once: true });
    entry.response.then(
      (response) => {
        signal.removeEventListener("abort", onAbort);
        resolve(response);
      },
      () => resolve(null)
    );
  });
}
